import React, {forwardRef, useMemo, useRef, useState, useEffect} from 'react';

import JSONEditor from '../../JSONEditor';
import {ContentCard, ContentToolbar} from '../components';
import {aceEditorReadOnly, toggleAceReadOnly} from '../utils';
import {createDefaultContent} from './utils';
import {
  useCreateBlock,
  useUpdateBlockContentJson,
} from '../hooks';
import {BaseBlock, ContentActions} from '../types';
import {defaultContentActions} from '../constants';
import {DEFAULT_MODE, codeBlockContentOptions} from './constants';
import SelectMode from './SelectMode';
import {CodeBlockContentOptions} from './types';
import UploadCSV from './UploadCSV';

import './CodeBlock.scss';

interface CodeBlockProps {
  content: BaseBlock;
  options?: CodeBlockContentOptions;
  actions?: ContentActions;
}

export const CodeBlock = forwardRef(({
  content,
  options = codeBlockContentOptions,
  actions = defaultContentActions,
}: CodeBlockProps, ref: any) => {
  const [mode, setMode] = useState(content.mode || DEFAULT_MODE);

  options = useMemo((): CodeBlockContentOptions => ({...codeBlockContentOptions, ...options}),
    [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const editorRef = useRef<any>();
  const modeRef = useRef(content.mode);
  const uid = content.uid;

  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);

  useCreateBlock(content, createDefaultContent());

  const onValueChange = (value: string) => {
    if (aceEditorReadOnly(editorRef.current?.editor)) {
      return;
    }
    updateBlockContentJson('value', value);
  };

  const onModeChange = (value: any) => {
    const aceEditor = editorRef.current?.editor || null;
    const mode = value.value;

    updateBlockContentJson('mode', mode);
    modeRef.current = mode;
    setMode(mode);

    if (!aceEditor) {
      return;
    }
    aceEditor.session.setUseWorker(mode === DEFAULT_MODE); // DEFAULT_MODE is JSON
  }

  const onCSVUpload = (value: string) => {
    updateBlockContentJson('value', value);

    if (editorRef.current?.editor) {
      editorRef.current.editor.setValue(value, -1);
    }
  }

  const onPreview = (data: any) => {
    const aceEditor = editorRef.current?.editor || null;

    if (!aceEditor) {
      return;
    }

    if (!data) {
      // restore
      toggleAceReadOnly(aceEditor, null, false);
    } else {
      try {
        toggleAceReadOnly(aceEditor, JSON.stringify(data, null, 2), true);
      } catch (e: any) {
        toggleAceReadOnly(aceEditor, e.message, true);
      }
    }
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={content.uid}
      disabled={options.readOnly}
      noContentElement={undefined}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__code-block">
        <ContentToolbar
          show={options?.showToolbar}
          contentId={uid as string}
          updating={updating}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false}
          disabled={options.readOnly || false}
          showSelect={false}
          showPreview={options.showPreview}
          onPreview={onPreview}
        />
        <div className="content-card__content__form">
          {
            options.showUploadCSV && (
              <UploadCSV
                uid={content.uid as string}
                mode={mode}
                onUpload={onCSVUpload}
              />
            )
          }
          <div className="editor">
            <JSONEditor
              ref={editorRef}
              defaultValue={content.value}
              onChange={onValueChange}
              disabled={options.readOnly}
              debounceTime={500}
              editorOptions={{
                useWorker: content.mode === DEFAULT_MODE,
              }}
            />
          </div>
          <div className="code-block-footer">
            <SelectMode
              selected={modeRef.current || DEFAULT_MODE}
              onChange={onModeChange}
              disabled={options.readOnly}
            />
          </div>
        </div>
      </div>
    </ContentCard>
  );
});
